import { ApolloError } from '@apollo/client'
import { useErrorHandler } from 'components/error'
import { useGerarLinkSistemaExternoMutation } from 'graphql/hooks.generated'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface SistemaExterno {
  id: string
  providerId: string
  nome: string
}

interface SistemaExternoLink {
  key: string
  url: string
}

export function useLinkSistemaExterno(sistemaExterno: SistemaExterno | null, cidadaoId?: ID) {
  const [link, setLink] = useState<SistemaExternoLink>({ key: '', url: '' })
  const handleError = useErrorHandler()
  const [gerarLink, { loading }] = useGerarLinkSistemaExternoMutation()
  const providerId = sistemaExterno?.providerId
  const sistemaExternoId = sistemaExterno?.id
  const sistemaExternoKey = useMemo(
    () => (providerId && sistemaExternoId ? `${providerId}:${sistemaExternoId}:${cidadaoId ?? ''}` : ''),
    [cidadaoId, providerId, sistemaExternoId]
  )
  const sistemaExternoKeyRef = useRef(sistemaExternoKey)
  const ultimaRequisicaoIdRef = useRef(0)

  useEffect(() => {
    sistemaExternoKeyRef.current = sistemaExternoKey
    ultimaRequisicaoIdRef.current++
  }, [sistemaExternoKey])

  useEffect(
    () => () => {
      ultimaRequisicaoIdRef.current++
    },
    []
  )

  const refresh = useCallback(async () => {
    const requisicaoId = ++ultimaRequisicaoIdRef.current

    if (!providerId || !sistemaExternoId) {
      setLink({ key: '', url: '' })
      return
    }

    const isRequisicaoAtual = () =>
      requisicaoId === ultimaRequisicaoIdRef.current && sistemaExternoKeyRef.current === sistemaExternoKey

    try {
      const { data } = await gerarLink({
        variables: {
          input: {
            providerId,
            sistemaExternoId,
            cidadaoId,
          },
        },
      })
      if (isRequisicaoAtual()) {
        setLink({ key: sistemaExternoKey, url: data?.gerarLinkSistemaExterno ?? '' })
      }
    } catch (e) {
      if (isRequisicaoAtual()) {
        handleError(e as ApolloError)
      }
    }
  }, [cidadaoId, gerarLink, handleError, providerId, sistemaExternoId, sistemaExternoKey])

  useEffect(() => {
    if (!sistemaExternoKey) {
      setLink({ key: '', url: '' })
    } else if (link.key !== sistemaExternoKey) {
      refresh()
    }
  }, [link.key, refresh, sistemaExternoKey])

  return { url: link.key === sistemaExternoKey ? link.url : '', refresh, loading }
}
